/* Common Imports */

import React from "react";

/* Component Imports */

import { Button, ButtonProps } from "@mui/material";
import { styled } from "@mui/system";

interface BeegruButtonProps extends ButtonProps {
	flavor?:
		| "primary"
		| "secondary"
		| "error"
		| "warning"
		| "info"
		| "success"
		| "boost"
		| "broadcast"
		| "intro"
		| "brown"
		| "ai";
}

/* Primary Button Styles */

const PrimaryButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: theme.palette.primary.main,
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#ca6614",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&:active": {
			background: theme.palette.primary.main,
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&:focus": {
			background: theme.palette.primary.main,
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&.Mui-disabled": {
			background: "#BDBDBD",
			color: "#858585",
		},
	},
	"&.MuiButton-outlined": {
		color: theme.palette.primary.main,
		border: `1px solid ${theme.palette.primary.main}`,
		background: "none",
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#674F3A" : "#feeada",
		},
		"&:active": {
			background: "none",
		},
		"&:focus": {
			background: "none",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
			border: "1px solid #BDBDBD",
		},
	},
	"&.MuiButton-text": {
		color: theme.palette.primary.main,
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#58402b" : "#feeada",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
		},
	},
}));

/* Secondary Button Styles */

const SecondaryButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	fontSize: "0.875rem",
	textTransform: "none",
	"&.MuiButton-contained": {
		background: "#FFFFFF",
		color: "#212B36",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#808080",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
			color: "#ffffff",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "#FFFFFF",
		color: "#212B36",
		border: "1px solid #919eab52",
		"&:hover": {
			background: "#919eab14",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		color: theme.palette.mode == "dark" ? "#FFFFFF" : "#212B36",
		"&:hover": {
			background: "none",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

/* Error Button Styles */

const ErrorButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: "#F44336",
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#0C53B7",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "#FFFFFF",
		color: "#F44336",
		border: "1px solid #F44336",
		"&:hover": {
			background: "#F44336",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		color: "#F44336",
		"&:hover": {
			background: "none",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

/* Warning Button Styles */

const WarningButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: "#1890FF",
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#0C53B7",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "#FFFFFF",
		color: "#1890FF",
		border: "1px solid #1890ff7a",
		"&:hover": {
			background: "#1890ff14",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		background: theme.palette.mode == "dark" ? "#FFFFFF" : "#212B36",
		color: "#1890FF",
		"&:hover": {
			background: "none",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

/* Info Button Styles */

const InfoButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: "#1890FF",
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#0C53B7",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "#FFFFFF",
		color: "#1890FF",
		border: "1px solid #1890ff7a",
		"&:hover": {
			background: "#1890ff14",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		background: "none",
		color: "#1890FF",
		"&:hover": {
			background: "none",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

/* Success Button Styles */

const SuccessButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: theme.palette.success.main,
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#157f57",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "none",
		color: theme.palette.success.light,
		border: `1px solid ${theme.palette.success.light}`,
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#157f57" : "#e8f6f1",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		background: "none",
		color: theme.palette.success.light,
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#157f57" : "#e8f6f1",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

/* Boost Button Styles */

const BoostButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: "#693a70",
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#502761",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&.Mui-disabled": {
			background: "#BDBDBD",
			color: "#858585",
		},
	},
	"&.MuiButton-outlined": {
		color: "#693a70",
		border: `1px solid #693a70`,
		background: "none",
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#674F3A" : "#f3f0f4",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
			border: "1px solid #BDBDBD",
		},
	},
	"&.MuiButton-text": {
		color: "#693a70",
		"&:hover": {
			background: "none",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
		},
	},
}));

/* Broadcast Button Styles */

const BroadcastButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	textTransform: "none",
	fontSize: "0.875rem",
	"&.MuiButton-contained": {
		background: "#621B16",
		color: "#FFFFFF",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#621B16",
			boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&.Mui-disabled": {
			background: "#BDBDBD",
			color: "#858585",
		},
	},
	"&.MuiButton-outlined": {
		color: "#621B16",
		border: `1px solid #621B16`,
		background: "none",
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#674F3A" : "#feeada",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
			border: "1px solid #BDBDBD",
		},
	},
	"&.MuiButton-text": {
		color: "#621B16",
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#58402b" : "#feeada",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
		},
	},
}));

const IntroButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "8px",
	textTransform: "none",
	fontSize: "0.813rem",
	padding: "4px 10px",
	"&.MuiButton-contained": {
		background: "#FFF7F0",
		color: "#6E3037",
		// boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "#FFF7F0",
			// boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		},
		"&.Mui-disabled": {
			background: "#BDBDBD",
			color: "#858585",
		},
	},
	"&.MuiButton-outlined": {
		color: "#FFF7F0",
		border: `1px solid #FFF7F0`,
		background: "none",
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#674F3A" : "#feeada",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
			border: "1px solid #BDBDBD",
		},
	},
	"&.MuiButton-text": {
		color: "#6E3037",
		"&:hover": {
			background: theme.palette.mode === "dark" ? "#58402b" : "#feeada",
		},
		"&.Mui-disabled": {
			color: "#BDBDBD",
		},
	},
}));

/* Dashboard Button */
const BrownButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	fontSize: "0.875rem",
	textTransform: "none",
	"&.MuiButton-contained": {
		background: "rgba(98, 27, 22, 0.8)",
		color: "#ffffff",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "rgba(98, 27, 22, 0.8)",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "#FFFFFF",
		color: "#212B36",
		border: "1px solid #919eab52",
		"&:hover": {
			background: "#919eab14",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		padding: "0rem",
		color: "#B05920",
		"&:hover": {
			background: "none",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

const AiButton = styled(Button)<BeegruButtonProps>(({ theme, flavor }) => ({
	borderRadius: "12px",
	fontSize: "0.875rem",
	textTransform: "none",
	"&.MuiButton-contained": {
		background: "var(--Linear, linear-gradient(203deg, #F1A95F 28.53%, #E36C26 71.47%))",
		color: "#ffffff",
		boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
		"&:hover": {
			background: "var(--Linear, linear-gradient(203deg, #F1A95F 28.53%, #E36C26 71.47%))",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
		},
	},
	"&.MuiButton-outlined": {
		background: "#FFFFFF",
		color: "#212B36",
		border: "1px solid #919eab52",
		"&:hover": {
			background: "#919eab14",
		},
		"&:disabled": {
			background: "#919eab3d",
			color: "#919eabcc",
			border: "1px solid #919eab3d",
		},
	},
	"&.MuiButton-text": {
		padding: "0rem",
		color: "#FFFFFF",
		"&:hover": {
			background: "none",
		},
		"&:disabled": {
			color: "#919eabcc",
		},
	},
}));

const BeegruButton = React.forwardRef<React.ComponentProps<typeof React.Fragment>, BeegruButtonProps>(
	function BeegruButton(props: BeegruButtonProps, ref: any) {
		const { children } = props;

		return (
			<React.Fragment>
				{/* Primary Button */}

				{(props.flavor === "primary" || props.flavor === undefined) && (
					<PrimaryButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</PrimaryButton>
				)}

				{/* Secondary Button */}

				{props.flavor === "secondary" && (
					<SecondaryButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</SecondaryButton>
				)}

				{/* Error Button */}

				{props.flavor === "error" && (
					<ErrorButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</ErrorButton>
				)}

				{/* Warning Button */}

				{props.flavor === "warning" && (
					<WarningButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</WarningButton>
				)}

				{/* Info Button */}

				{props.flavor === "info" && (
					<InfoButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</InfoButton>
				)}

				{/* Success Button */}

				{props.flavor === "success" && (
					<SuccessButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</SuccessButton>
				)}

				{/* Boost Button */}

				{props.flavor === "boost" && (
					<BoostButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</BoostButton>
				)}

				{props.flavor === "broadcast" && (
					<BroadcastButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</BroadcastButton>
				)}

				{/* Intro Button */}
				{props.flavor === "intro" && (
					<IntroButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						disableTouchRipple
						disableElevation
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</IntroButton>
				)}
				{/* Brown Button */}
				{props.flavor === "brown" && (
					<BrownButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						disableTouchRipple
						disableElevation
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</BrownButton>
				)}
				{props.flavor === "ai" && (
					<AiButton
						variant={props.variant ? props.variant : "contained"}
						size={props.size ? props.size : "large"}
						disableTouchRipple
						disableElevation
						{...props}
						ref={ref}
					>
						{children ? children : ""}
					</AiButton>
				)}
			</React.Fragment>
		);
	},
) as React.ForwardRefExoticComponent<any>;

export default BeegruButton;
